// Approximate chars that fit in a given pixel width at a given font size.
// Ratio tuned for Inter / system-ui (the font stack our rasterized SVGs
// now declare). Inter averages ~0.48 × fontSize wide for mixed text; the
// previous 0.55 value was tuned for a wider/serif fallback and was
// over-truncating labels that actually fit. We keep a small safety
// margin by going with 0.50 rather than a true 0.48 — better to clip a
// character early than overflow into the neighbouring element, which
// the responsive SVG can't clip away.
export function maxCharsForWidth(px: number, fontSize: number): number {
  const avg = fontSize * 0.5;
  return Math.max(1, Math.floor(px / avg));
}

export function truncate(
  text: string | null | undefined,
  maxChars: number
): string {
  if (!text) return "";
  if (text.length <= maxChars) return text;
  return `${text.slice(0, Math.max(1, maxChars - 1))}…`;
}

const joinWord = (line: string, word: string) =>
  line ? `${line} ${word}` : word;

// Hard-break an over-long word into full-width chunks. Returns the remainder
// that still fits on a line, and whether the line budget ran out mid-word.
function hardBreakWord(
  word: string,
  maxChars: number,
  maxLines: number,
  lines: string[],
  commit: () => void
): { rest: string; exhausted: boolean } {
  let rest = word;
  while (rest.length > maxChars) {
    commit();
    if (lines.length >= maxLines) return { rest, exhausted: true };
    lines.push(rest.slice(0, maxChars));
    rest = rest.slice(maxChars);
    if (lines.length >= maxLines) return { rest, exhausted: true };
  }
  return { rest, exhausted: false };
}

function buildLines(
  words: string[],
  maxChars: number,
  maxLines: number
): string[] {
  const lines: string[] = [];
  let current = "";
  const commit = () => {
    if (current) {
      lines.push(current);
      current = "";
    }
  };

  for (const original of words) {
    const { rest, exhausted } = hardBreakWord(
      original,
      maxChars,
      maxLines,
      lines,
      commit
    );
    if (exhausted) break;

    const trial = joinWord(current, rest);
    if (trial.length <= maxChars) {
      current = trial;
    } else {
      commit();
      if (lines.length >= maxLines) break;
      current = rest;
    }
  }
  if (current && lines.length < maxLines) commit();
  return lines;
}

// Word-wrap `text` into at most `maxLines` lines of up to `maxChars` each.
// Words longer than a line are hard-broken mid-word. If the text still
// overflows after filling all lines, the last line is ellipsised.
// Returns an array of line strings — callers render each as a <tspan>.
export function wrapText(
  text: string | null | undefined,
  maxChars: number,
  maxLines = 2
): string[] {
  if (!text) return [];
  if (maxChars <= 0 || maxLines <= 0) return [];

  const words = text.trim().split(/\s+/).filter(Boolean);
  const capped = buildLines(words, maxChars, maxLines).slice(0, maxLines);

  // If the rendered text is shorter than the source, the last line overflowed
  // — ellipsise so we don't silently drop characters.
  const rendered = capped.join(" ").length;
  const sourceLen = text.trim().replace(/\s+/g, " ").length;
  if (rendered < sourceLen && capped.length > 0) {
    const last = capped[capped.length - 1];
    capped[capped.length - 1] =
      last.length >= maxChars
        ? `${last.slice(0, Math.max(1, maxChars - 1))}…`
        : `${last}…`;
  }
  return capped;
}
